import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import CTable from "../../../components/Table/table";
import { getAllExamsRequest } from "../../../modules/admin-modules/exams/Actions";
import {
  getPaidUnpaidStudentsRequest,
  getStudentsNotEnteredExamRequest,
  getStudentsNotViewedVideosRequest,
  sendAttendanceCustomMessageRequest,
} from "../../../modules/admin-modules/reports/Actions";
import {
  getAllVideosRequest,
  getPaymentSubjectRequest,
} from "../../../modules/admin-modules/videos/Actions";

const StudentCheckbox = React.memo(({ studentId, isSelected, onToggle }) => (
  <Checkbox
    isChecked={isSelected}
    onChange={(e) => onToggle(studentId, e.target.checked)}
  />
));

const colourStyles = {
  control: (styles) => ({
    ...styles,
    width: "100%",
    minWidth: "200px",
    background: "#f5f5f5",
    color: "#690000",
    borderRadius: "16px",
  }),
  option: (styles) => ({ ...styles, color: "#690000" }),
};

const getStudentId = (data) => data?.id || data?.student_id || data?.user_id;

const PAGE_SIZE = 20;

const joinNames = (items) =>
  (items || [])
    .map((item) => item?.label)
    .filter(Boolean)
    .join(" و ");

const buildPresetMessage = (
  mode,
  videosSelected,
  examsSelected,
  paidSubjects,
  unpaidSubjects
) => {
  if (mode === "videos") {
    const names = joinNames(videosSelected);
    return names ? `الطالب لم يشاهد ${names}` : "";
  }
  if (mode === "exams") {
    const names = joinNames(examsSelected);
    return names ? `الطالب لم يمتحن ${names}` : "";
  }
  const paid = joinNames(paidSubjects);
  const unpaid = joinNames(unpaidSubjects);
  if (paid && unpaid) return `الطالب دفع ${paid} ولم يدفع ${unpaid}`;
  if (unpaid) return `الطالب لم يدفع ${unpaid}`;
  if (paid) return `الطالب دفع ${paid}`;
  return "";
};

const TAB_MODES = ["videos", "exams", "payments"];

const MissedActivity = () => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState("videos");
  const [videosSelected, setVideosSelected] = useState([]);
  const [examsSelected, setExamsSelected] = useState([]);
  const [paymentSubjectsSelected, setPaymentSubjectsSelected] = useState([]);
  const [paidSubjects, setPaidSubjects] = useState([]);
  const [unpaidSubjects, setUnpaidSubjects] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [message, setMessage] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageNumber, setPageNumber] = useState(0);

  const videos = useSelector((state) => state.videos.videos);
  const exams = useSelector((state) => state.exams.exams);
  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);
  const studentsNotViewedVideos = useSelector(
    (state) => state.reports.studentsNotViewedVideos
  );
  const studentsNotEnteredExam = useSelector(
    (state) => state.reports.studentsNotEnteredExam
  );
  const paidUnpaidStudents = useSelector(
    (state) => state.reports.paidUnpaidStudents
  );
  const isLoading = useSelector((state) => state.reports.isLoading);

  const students =
    mode === "videos"
      ? studentsNotViewedVideos
      : mode === "exams"
      ? studentsNotEnteredExam
      : paidUnpaidStudents;

  useEffect(() => {
    dispatch(getAllVideosRequest());
    dispatch(getAllExamsRequest());
    dispatch(getPaymentSubjectRequest());
  }, [dispatch]);

  useEffect(() => {
    setSelectedStudents([]);
    setSearchQuery("");
    setPageNumber(0);
  }, [students, mode]);

  useEffect(() => {
    setMessage(
      buildPresetMessage(
        mode,
        videosSelected,
        examsSelected,
        paidSubjects,
        unpaidSubjects
      )
    );
  }, [mode, videosSelected, examsSelected, paidSubjects, unpaidSubjects]);

  const videosOptions = (Array.isArray(videos) ? videos : []).map((data) => ({
    value: data.id,
    label: data.title || data.name || `#${data.id}`,
  }));

  const examsOptions = (Array.isArray(exams) ? exams : []).map((data) => ({
    value: data.id,
    label: data.name || `#${data.id}`,
  }));

  const paymentSubjectsOptions = (
    Array.isArray(paymentSubjects) ? paymentSubjects : []
  ).map((data) => ({
    value: data.id,
    label: data.name,
  }));

  const handleFetch = () => {
    if (mode === "videos") {
      if (!videosSelected.length) return;
      dispatch(
        getStudentsNotViewedVideosRequest({
          video_ids: videosSelected.map((item) => item.value),
        })
      );
    } else if (mode === "exams") {
      if (!examsSelected.length) return;
      const payload = {
        exam_ids: examsSelected.map((item) => item.value),
      };
      if (paymentSubjectsSelected.length) {
        payload.payment_subject_ids = paymentSubjectsSelected.map(
          (item) => item.value
        );
      }
      dispatch(getStudentsNotEnteredExamRequest(payload));
    } else {
      if (!paidSubjects.length && !unpaidSubjects.length) return;
      dispatch(
        getPaidUnpaidStudentsRequest({
          paid: paidSubjects.map((item) => item.value),
          unpaid: unpaidSubjects.map((item) => item.value),
        })
      );
    }
    setHasSearched(true);
  };

  const handleStudentToggle = useCallback((studentId, isChecked) => {
    setSelectedStudents((prev) => {
      if (isChecked) return [...prev, studentId];
      return prev.filter((id) => id !== studentId);
    });
  }, []);

  const filteredStudents = useMemo(() => {
    const list = students || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((student) => {
      const name = String(student?.name || "").toLowerCase();
      const phone = String(student?.phone || "");
      const parentPhone = String(student?.parent_phone || "");
      return (
        name.includes(query) ||
        phone.includes(query) ||
        parentPhone.includes(query)
      );
    });
  }, [students, searchQuery]);

  const handleSelectAll = useCallback(
    (isChecked) => {
      const ids = filteredStudents
        .map((student) => getStudentId(student))
        .filter(Boolean);
      if (isChecked) {
        setSelectedStudents((prev) => Array.from(new Set([...prev, ...ids])));
      } else {
        const remove = new Set(ids);
        setSelectedStudents((prev) => prev.filter((id) => !remove.has(id)));
      }
    },
    [filteredStudents]
  );

  const filteredIds = useMemo(
    () =>
      filteredStudents
        .map((student) => getStudentId(student))
        .filter(Boolean),
    [filteredStudents]
  );

  const totalCount = filteredIds.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(pageNumber, totalPages - 1);
  const pageStart = currentPage * PAGE_SIZE;

  const selectedSet = useMemo(
    () => new Set(selectedStudents),
    [selectedStudents]
  );

  const handleSendMessage = () => {
    if (!message.trim()) {
      toast.error("أدخل الرسالة أولاً");
      return;
    }
    if (!selectedStudents.length) {
      toast.error("اختر طالب واحد على الأقل");
      return;
    }
    dispatch(
      sendAttendanceCustomMessageRequest({
        user_ids: selectedStudents,
        message: message.trim(),
      })
    );
  };

  const Data = useMemo(
    () =>
      filteredStudents.slice(pageStart, pageStart + PAGE_SIZE).map((data, index) => {
        const studentId = getStudentId(data);
        return {
          ...data,
          index: `#${pageStart + index + 1}`,
          id: studentId,
          select: (
            <StudentCheckbox
              studentId={studentId}
              isSelected={selectedSet.has(studentId)}
              onToggle={handleStudentToggle}
            />
          ),
          name: data?.name,
          username: data?.username || data?.user_name,
          phone: data?.phone,
          parent_phone: data?.parent_phone,
          group_name:
            data?.group?.name ||
            data?.student_group?.name ||
            data?.group_name ||
            "",
        };
      }),
    [filteredStudents, pageStart, selectedSet, handleStudentToggle]
  );

  const columns = [
    { Header: "اختيار", accessor: "select" },
    { Header: "#", accessor: "index" },
    { Header: "اسم الطالب", accessor: "name" },
    { Header: "كود الطالب", accessor: "username" },
    { Header: "المجموعة", accessor: "group_name" },
    { Header: "رقم الطالب", accessor: "phone" },
    { Header: "رقم ولي الأمر", accessor: "parent_phone" },
  ];

  const selectedInFilterCount = filteredIds.filter((id) =>
    selectedSet.has(id)
  ).length;

  const allSelected =
    totalCount > 0 && selectedInFilterCount === totalCount;

  const canFetch =
    mode === "videos"
      ? videosSelected.length > 0
      : mode === "exams"
      ? examsSelected.length > 0
      : paidSubjects.length > 0 || unpaidSubjects.length > 0;

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="100"
          flexWrap="wrap"
          gap={3}
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            النشاط الفائت
          </Text>
          <LogoutMenu />
        </Flex>

        <Tabs
          index={Math.max(0, TAB_MODES.indexOf(mode))}
          onChange={(index) => {
            setMode(TAB_MODES[index] || "videos");
            setHasSearched(false);
            setSelectedStudents([]);
          }}
          colorScheme="red"
          mb={4}
        >
          <TabList>
            <Tab fontWeight="600">لم يشاهدوا الفيديوهات</Tab>
            <Tab fontWeight="600">لم يدخلوا الامتحان</Tab>
            <Tab fontWeight="600">الدفع وعدم الدفع</Tab>
          </TabList>
        </Tabs>

        <Flex
          w="100%"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          mb={4}
        >
          {mode === "videos" ? (
            <Box flex="1" minW="240px">
              <Select
                options={videosOptions}
                isMulti
                value={videosSelected}
                onChange={(data) => setVideosSelected(data || [])}
                styles={colourStyles}
                placeholder="الفيديوهات"
              />
            </Box>
          ) : mode === "exams" ? (
            <>
              <Box flex="1" minW="240px">
                <Select
                  options={examsOptions}
                  isMulti
                  value={examsSelected}
                  onChange={(data) => setExamsSelected(data || [])}
                  styles={colourStyles}
                  placeholder="الامتحانات"
                />
              </Box>
              <Box flex="1" minW="240px">
                <Select
                  options={paymentSubjectsOptions}
                  isMulti
                  value={paymentSubjectsSelected}
                  onChange={(data) => setPaymentSubjectsSelected(data || [])}
                  styles={colourStyles}
                  placeholder="المدفوعات (اختياري)"
                />
              </Box>
            </>
          ) : (
            <>
              <Box flex="1" minW="240px">
                <Select
                  options={paymentSubjectsOptions}
                  isMulti
                  value={paidSubjects}
                  onChange={(data) => setPaidSubjects(data || [])}
                  styles={colourStyles}
                  placeholder="المدفوعات المدفوعة"
                />
              </Box>
              <Box flex="1" minW="240px">
                <Select
                  options={paymentSubjectsOptions}
                  isMulti
                  value={unpaidSubjects}
                  onChange={(data) => setUnpaidSubjects(data || [])}
                  styles={colourStyles}
                  placeholder="المدفوعات غير المدفوعة"
                />
              </Box>
            </>
          )}
          <Button
            colorScheme="red"
            borderRadius="16px"
            onClick={handleFetch}
            isDisabled={!canFetch}
            isLoading={isLoading}
          >
            عرض
          </Button>
        </Flex>

        {students?.length > 0 && (
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPageNumber(0);
            }}
            placeholder="بحث بالاسم أو رقم الهاتف"
            bg="#f5f5f5"
            borderRadius="12px"
            mb={4}
            maxW="420px"
          />
        )}

        {students?.length > 0 && (
          <Box
            mb={4}
            p={4}
            bg="white"
            borderRadius="md"
            border="1px solid #e2e8f0"
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              flexWrap="wrap"
              gap={2}
            >
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={
                  selectedInFilterCount > 0 &&
                  selectedInFilterCount < totalCount
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                تحديد الكل ({selectedInFilterCount}/{totalCount})
              </Checkbox>
              <Button
                colorScheme="orange"
                onClick={handleSendMessage}
                isDisabled={!selectedStudents.length || !message.trim()}
                isLoading={isLoading}
              >
                إرسال الرسالة
              </Button>
            </Flex>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="اكتب الرسالة المخصصة..."
              rows={4}
              bg="#f5f5f5"
              borderRadius="12px"
            />
          </Box>
        )}

        {hasSearched && !isLoading && students?.length === 0 && (
          <Box p={6} textAlign="center" bg="gray.50" borderRadius="md" mb={4}>
            <Text fontSize="16px" color="gray.600">
              لا يوجد طلاب
            </Text>
          </Box>
        )}

        {students?.length > 0 && filteredStudents.length === 0 && (
          <Box p={6} textAlign="center" bg="gray.50" borderRadius="md" mb={4}>
            <Text fontSize="16px" color="gray.600">
              لا توجد نتائج مطابقة
            </Text>
          </Box>
        )}

        {totalCount > PAGE_SIZE && (
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            flexWrap="wrap"
            gap={2}
          >
            <Text fontSize="14px" color="gray.600">
              {pageStart + 1} - {Math.min(pageStart + PAGE_SIZE, totalCount)} من{" "}
              {totalCount}
            </Text>
            <Flex gap={2} alignItems="center">
              <Button
                size="sm"
                onClick={() => setPageNumber((page) => Math.max(0, page - 1))}
                isDisabled={currentPage === 0}
              >
                السابق
              </Button>
              <Text fontSize="14px">
                {currentPage + 1} / {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() =>
                  setPageNumber((page) => Math.min(totalPages - 1, page + 1))
                }
                isDisabled={currentPage >= totalPages - 1}
              >
                التالي
              </Button>
            </Flex>
          </Flex>
        )}

        <CTable
          selectedData={() => {}}
          Columns={columns}
          Data={Data}
          path={"openModel"}
          Actions
          Title={``}
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar={true}
          noFilter={true}
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={() => {}}
          currentpage={0}
          setPageNumber={() => {}}
          noSecondSearchBar={true}
          isLoading={isLoading}
          totalPage={1}
          hideFooter
        />
      </div>
    </Layout>
  );
};

export default MissedActivity;
